"use client"

import { useState, useEffect } from "react"
import { AuthLayout } from "@/components/auth/auth-layout"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useRouter } from "@/src/hooks/useRouter"
import { useAuth } from "@/src/contexts/AuthContext"
import { useKyc } from "@/src/contexts/KycContext"
import { User, Building2, CheckCircle2, FileText } from "lucide-react"
import { verifyCompany, searchCompanyByTaxId, CompanyKycData } from "@/lib/api/registration"
import DocumentUploader from "@/components/kyc/DocumentUploader"
import DocumentList from "@/components/kyc/DocumentList"
import type { KycDocument } from "@/lib/api/kyc-documents"
import { useThaiLocationForm } from "@/hooks/useThaiLocationForm"
import { ThaiLocationSelect } from "@/components/forms/ThaiLocationSelect"

export default function VerifyCompanyPage() {
  const router = useRouter()
  const { refreshUser } = useAuth()
  const { kycStatus } = useKyc()
  const [loading, setLoading] = useState(false)
  const [searching, setSearching] = useState(false)
  const [error, setError] = useState("")
  const [success, setSuccess] = useState(false)
  const [token, setToken] = useState<string | null>(null)
  
  // Location form state (Thai provinces, amphures, districts)
  const {
    formData: locationData,
    loading: locationLoading,
    provinces,
    amphures,
    districts,
    handleProvinceChange,
    handleAmphureChange,
    handleDistrictChange,
    setData: setLocationData
  } = useThaiLocationForm({
    onError: (error) => {
      console.error('Location API error:', error)
      setError('ไม่สามารถโหลดข้อมูลที่อยู่ได้ กรุณาลองใหม่อีกครั้ง')
    }
  })

  // Tax invoice delivery location form state (separate cascading dropdowns)
  const {
    formData: taxLocationData,
    loading: taxLocationLoading,
    provinces: taxProvinces,
    amphures: taxAmphures,
    districts: taxDistricts,
    handleProvinceChange: handleTaxProvinceChange,
    handleAmphureChange: handleTaxAmphureChange,
    handleDistrictChange: handleTaxDistrictChange,
    setData: setTaxLocationData,
    reset: resetTaxLocation,
  } = useThaiLocationForm({
    onError: (error) => {
      console.error('Tax delivery location API error:', error)
      setError('ไม่สามารถโหลดข้อมูลที่อยู่ได้ กรุณาลองใหม่อีกครั้ง')
    }
  })
  
  // Company info form state
  const [formData, setFormData] = useState({
    businessType: "",
    taxId: "",
    companyName: "",
    address: "",
    branch: "",
    contactPerson: "",
  })
  const [useAddressForTax, setUseAddressForTax] = useState<boolean | null>(null)
  const [taxInvoiceDeliveryData, setTaxInvoiceDeliveryData] = useState({
    address: "",
  })
  
  // Document upload state
  const [uploadedDocuments, setUploadedDocuments] = useState<{
    companyRegistration?: KycDocument;
    directorIdCard?: KycDocument;
    additionalDocument?: KycDocument;
  }>({})
  const [refreshDocuments, setRefreshDocuments] = useState(0)

  useEffect(() => {
    const accessToken = localStorage.getItem("accessToken") || localStorage.getItem("authToken")
    if (!accessToken) {
      router.push("/auth/login")
      return
    }
    setToken(accessToken)
  }, [router])

  // Pre-fill form if KYC was rejected
  useEffect(() => {
    if (kycStatus?.status === 'rejected' && kycStatus.kycData) {
      const data = kycStatus.kycData
      
      // Pre-fill company info
      setFormData({
        businessType: data.businessType || "",
        taxId: data.taxId || "",
        companyName: data.companyName || "",
        address: data.address || "",
        branch: data.branch || "",
        contactPerson: data.contactPerson || "",
      })
      
      // Pre-fill location data
      if (data.province && data.subDistrict && data.district) {
        setLocationData({
          province: data.province,
          amphure: data.subDistrict,  // Backend: subDistrict → Frontend: amphure
          tambon: data.district,      // Backend: district → Frontend: tambon
          postalCode: data.postalCode || ""
        })
      }

      setTaxInvoiceDeliveryData({
        address: data.taxInvoiceDeliveryAddress || "",
      })

      if (
        data.taxInvoiceDeliveryProvince &&
        data.taxInvoiceDeliverySubDistrict &&
        data.taxInvoiceDeliveryDistrict
      ) {
        setTaxLocationData({
          province: data.taxInvoiceDeliveryProvince,
          amphure: data.taxInvoiceDeliverySubDistrict,
          tambon: data.taxInvoiceDeliveryDistrict,
          postalCode: data.taxInvoiceDeliveryPostalCode || "",
        })
      }
      
      setUseAddressForTax(data.useAddressForTax !== false)
    }
  }, [kycStatus])

  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
    setError("")
  }

  const handleAddressTypeChange = (useTaxAddress: boolean) => {
    setUseAddressForTax(useTaxAddress)
    setError("")
    if (useTaxAddress) {
      setTaxInvoiceDeliveryData({
        address: "",
      })
      resetTaxLocation()
    }
  }

  const handleTaxDeliveryInputChange = (field: "address", value: string) => {
    setTaxInvoiceDeliveryData((prev) => ({ ...prev, [field]: value }))
    setError("")
  }

  const handleSearch = async () => {
    if (!formData.taxId || !formData.taxId.match(/^\d{13}$/)) {
      setError("กรุณากรอกเลขผู้เสียภาษี 13 หลัก")
      return
    }

    if (!token) {
      setError("กรุณาเข้าสู่ระบบใหม่")
      router.push("/auth/login")
      return
    }

    try {
      setSearching(true)
      setError("")
      const response = await searchCompanyByTaxId(token, formData.taxId)

      if (response.found) {
        setFormData((prev) => ({
          ...prev,
          companyName: response.data.companyName || prev.companyName,
          businessType: response.data.businessType || prev.businessType,
          address: response.data.address || prev.address,
        }))
        // Note: Location data auto-fill from company search is not supported with cascading dropdowns
        // User will need to manually select province → amphure → district
      } else {
        setError("ไม่พบข้อมูลบริษัทในระบบ กรุณากรอกข้อมูลด้วยตนเอง")
      }
    } catch (err: any) {
      setError("ไม่พบข้อมูลบริษัทในระบบ กรุณากรอกข้อมูลด้วยตนเอง")
    } finally {
      setSearching(false)
    }
  }

  const handleBack = () => {
    router.push("/auth/verify")
  }

  const handleNext = async () => {
    setError("")
    
    // Validation
    if (!formData.taxId || !formData.taxId.match(/^\d{13}$/)) {
      setError("กรุณากรอกเลขผู้เสียภาษี 13 หลัก")
      return
    }
    if (!formData.companyName) {
      setError("กรุณากรอกชื่อบริษัท")
      return
    }
    if (!formData.businessType) {
      setError("กรุณาเลือกประเภทธุรกิจ")
      return
    }
    if (!formData.contactPerson) {
      setError("กรุณากรอกชื่อบุคคล")
      return
    }
    // Validate location data from Thai location dropdowns
    if (!locationData.tambon || !locationData.amphure || !locationData.province) {
      setError("กรุณาเลือกข้อมูลที่อยู่ให้ครบถ้วน")
      return
    }
    if (!locationData.postalCode || locationData.postalCode.length !== 5) {
      setError("กรุณาเลือกตำบลเพื่อให้ระบบกรอกรหัสไปรษณีย์อัตโนมัติ")
      return
    }
    if (useAddressForTax === null) {
      setError("กรุณาเลือกที่อยู่สำหรับจัดส่งใบกำกับภาษี")
      return
    }
    if (useAddressForTax === false) {
      if (!taxInvoiceDeliveryData.address.trim()) {
        setError("กรุณากรอกที่อยู่สำหรับจัดส่งใบกำกับภาษี")
        return
      }
      if (!taxLocationData.tambon.trim()) {
        setError("กรุณาเลือกแขวง/ตำบลสำหรับจัดส่งใบกำกับภาษี")
        return
      }
      if (!taxLocationData.amphure.trim()) {
        setError("กรุณาเลือกเขต/อำเภอสำหรับจัดส่งใบกำกับภาษี")
        return
      }
      if (!taxLocationData.province.trim()) {
        setError("กรุณาเลือกจังหวัดสำหรับจัดส่งใบกำกับภาษี")
        return
      }
      if (!/^\d{5}$/.test(taxLocationData.postalCode.trim())) {
        setError("กรุณาเลือกตำบลสำหรับจัดส่งใบกำกับภาษี เพื่อให้ระบบกรอกรหัสไปรษณีย์อัตโนมัติ")
        return
      }
    }
    // Validate document uploads - require company registration and director ID card
    if (!uploadedDocuments.companyRegistration) {
      setError("กรุณาอัพโหลดหนังสือจดทะเบียนบริษัท")
      return
    }
    if (!uploadedDocuments.directorIdCard) {
      setError("กรุณาอัพโหลดบัตรประชาชนกรรมการผู้มีอำนาจ")
      return
    }

    if (!token) {
      setError("กรุณาเข้าสู่ระบบใหม่")
      router.push("/auth/login")
      return
    }

    try {
      setLoading(true)
      
      // Map locationData fields to backend expected names
      const data: CompanyKycData = {
        companyName: formData.companyName,
        taxId: formData.taxId,
        businessType: formData.businessType as any,
        address: formData.address,
        subDistrict: locationData.amphure,  // Frontend: amphure → Backend: subDistrict
        district: locationData.tambon,      // Frontend: tambon → Backend: district
        province: locationData.province,    // Same name
        postalCode: locationData.postalCode, // Same name
        contactPerson: formData.contactPerson,
        useAddressForTax: useAddressForTax === true,
        taxInvoiceDeliveryAddress: useAddressForTax ? undefined : taxInvoiceDeliveryData.address.trim(),
        taxInvoiceDeliveryDistrict: useAddressForTax ? undefined : taxLocationData.tambon.trim(),
        taxInvoiceDeliverySubDistrict: useAddressForTax ? undefined : taxLocationData.amphure.trim(),
        taxInvoiceDeliveryProvince: useAddressForTax ? undefined : taxLocationData.province.trim(),
        taxInvoiceDeliveryPostalCode: useAddressForTax ? undefined : taxLocationData.postalCode.trim(),
      }

      await verifyCompany(token, data)
      setSuccess(true)

      const userData = localStorage.getItem("user")
      if (userData) {
        const user = JSON.parse(userData)
        user.kycVerified = true
        user.kycType = "company"
        localStorage.setItem("user", JSON.stringify(user))
      }

      // Refresh AuthContext to sync with localStorage
      await refreshUser()

      setTimeout(() => {
        router.push("/dashboard")
      }, 2000)
    } catch (err: any) {
      setError(err.message || "เกิดข้อผิดพลาดในการยืนยันตัวตน")
    } finally {
      setLoading(false)
    }
  }

  if (success) {
    return (
      <AuthLayout>
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto text-center">
          <div className="mb-6">
            <CheckCircle2 className="w-20 h-20 text-green-500 mx-auto" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            ยืนยันตัวตนสำเร็จ!
          </h1>
          <p className="text-gray-600 mb-6">
            ระบบได้บันทึกข้อมูลบริษัทของคุณเรียบร้อยแล้ว
            <br />
            กำลังนำคุณไปยังหน้าแดชบอร์ด...
          </p>
        </div>
      </AuthLayout>
    )
  }

  return (
    <AuthLayout>
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-[#323130] mb-2">
            กรอกข้อมูลสำหรับคำสั่งซื้อ
          </h1>
          <p className="text-[#949494] text-sm">ประเภทการจดทะเบียนบุริษัท</p>
        </div>

        {/* Type Selection Tabs */}
        <div className="flex gap-4 mb-8">
          <button
            onClick={() => router.push("/auth/verify/individual")}
            className="flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl border-2 border-[#e5e5e5] bg-white hover:border-[#d1d1d1] transition-all"
            disabled={loading}
          >
            <User className="w-6 h-6 text-[#949494]" />
            <span className="font-semibold text-[#949494]">บุคคลธรรมดา</span>
          </button>
          <button className="flex-1 flex items-center justify-center gap-3 px-6 py-4 rounded-xl border-2 border-[#4169E1] bg-[#E8F0FE] transition-all">
            <Building2 className="w-6 h-6 text-[#4169E1]" />
            <span className="font-semibold text-[#323130]">นิติบุคคล</span>
          </button>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm whitespace-pre-line">{error}</p>
          </div>
        )}

        {/* Form Section */}
        <div className="space-y-6">
          <h2 className="text-xl font-bold text-[#323130] mb-4">
            ข้อมูลเกี่ยวกับธุรกิจ
          </h2>

          {/* Business Type */}
          <div className="space-y-2">
            <Label htmlFor="businessType" className="text-[#323130] font-medium">
              ประเภทธุรกิจ <span className="text-red-500">*</span>
            </Label>
            <select
              id="businessType"
              value={formData.businessType}
              onChange={(e) => handleInputChange("businessType", e.target.value)}
              className="w-full px-3 py-1 rounded-lg border border-[#d1d1d1] bg-white text-[#323130] focus:border-[#4169E1] focus:outline-none transition-colors appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%234169E1' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "14px",
              }}
            >
              <option value="">กรุณาเลือกประเภทธุรกิจ</option>
              <option value="corporation">บริษัทจำกัด</option>
              <option value="partnership">ห้างหุ้นส่วน</option>
              <option value="cooperative">สหกรณ์</option>
              <option value="foundation">มูลนิธิ</option>
              <option value="other">อื่นๆ</option>
            </select>
          </div>

          <h2 className="text-xl font-bold text-[#323130] pt-4">
            กรอกข้อมูลสำหรับคำสั่งซื้อ
          </h2>
          <p className="text-sm text-[#949494] -mt-2">
            ค้นหาเลขผู้เสียภาษีอากรจากระบบ เพื่อกรอกข้อมูลที่เหลือโดยอัตโนมัติ
          </p>

          {/* Tax ID with Search */}
          <div className="space-y-2">
            <Label htmlFor="taxId" className="text-[#323130] font-medium">
              เลขประจำตัวผู้เสียภาษีอากร <span className="text-red-500">*</span>
            </Label>
            <div className="flex gap-2">
              <Input
                id="taxId"
                type="text"
                value={formData.taxId}
                onChange={(e) => handleInputChange("taxId", e.target.value.replace(/\D/g, ""))}
                placeholder="เลขประจำตัวผู้เสียภาษีอากร 13 หลัก"
                maxLength={13}
                className="flex-1 bg-white border-[#d1d1d1] text-[#323130]"
                disabled={loading}
              />
              <Button
                onClick={handleSearch}
                className="bg-[#4169E1] hover:bg-[#3155c8] text-white px-6"
                disabled={searching || loading || formData.taxId.length !== 13}
              >
                {searching ? "กำลังค้นหา..." : "ค้นหา"}
              </Button>
            </div>
          </div>

          {/* Company Name */}
          <div className="space-y-2">
            <Label htmlFor="companyName" className="text-[#323130] font-medium">
              ชื่อบริษัท <span className="text-red-500">*</span>
            </Label>
            <Input
              id="companyName"
              type="text"
              value={formData.companyName}
              onChange={(e) => handleInputChange("companyName", e.target.value)}
              placeholder="ชื่อบริษัท"
              className="bg-white border-[#d1d1d1] text-[#323130]"
              disabled={loading}
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label htmlFor="address" className="text-[#323130] font-medium">
              ที่อยู่ <span className="text-red-500">*</span>
            </Label>
            <Input
              id="address"
              type="text"
              value={formData.address}
              onChange={(e) => handleInputChange("address", e.target.value)}
              placeholder="ที่อยู่ เช่น เลขที่, หมู่บ้าน, อาคาร, ซอย"
              className="bg-white border-[#d1d1d1] text-[#323130]"
              disabled={loading}
            />
          </div>

          {/* Contact Person */}
          <div className="space-y-2">
            <Label htmlFor="contactPerson" className="text-[#323130] font-medium">
              ชื่อบุคคล <span className="text-red-500">*</span>
            </Label>
            <Input
              id="contactPerson"
              type="text"
              value={formData.contactPerson}
              onChange={(e) => handleInputChange("contactPerson", e.target.value)}
              placeholder="ชื่อบุคคล"
              className="bg-white border-[#d1d1d1] text-[#323130]"
              disabled={loading}
            />
          </div>

          {/* Thai Location Selection - Cascading Dropdowns */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <ThaiLocationSelect
                label="จังหวัด"
                value={locationData.province}
                onChange={handleProvinceChange}
                options={provinces}
                placeholder="เลือกจังหวัด"
                disabled={loading || locationLoading.provinces}
                loading={locationLoading.provinces}
                required
              />
            </div>
            <div className="space-y-2">
              <ThaiLocationSelect
                label="เขต / อำเภอ"
                value={locationData.amphure}
                onChange={handleAmphureChange}
                options={amphures}
                placeholder={locationData.provinceId ? "เลือกเขต / อำเภอ" : "กรุณาเลือกจังหวัดก่อน"}
                disabled={loading || !locationData.provinceId || locationLoading.amphures}
                loading={locationLoading.amphures}
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <ThaiLocationSelect
                label="แขวง / ตำบล"
                value={locationData.tambon}
                onChange={handleDistrictChange}
                options={districts}
                placeholder={locationData.amphureId ? "เลือกแขวง / ตำบล" : "กรุณาเลือกเขต / อำเภอก่อน"}
                disabled={loading || !locationData.amphureId || locationLoading.districts}
                loading={locationLoading.districts}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="postalCode" className="text-[#323130] font-medium">
                รหัสไปรษณีย์ <span className="text-red-500">*</span>
              </Label>
              <Input
                id="postalCode"
                type="text"
                value={locationData.postalCode}
                readOnly
                placeholder="จะกรอกอัตโนมัติเมื่อเลือกตำบล"
                disabled
                className="bg-gray-50 border-[#d1d1d1] text-[#323130]"
              />
            </div>
          </div>

          {/* Branch */}
          <div className="space-y-2">
            <Label htmlFor="branch" className="text-[#323130] font-medium">
              สาขา <span className="text-red-500">*</span>
            </Label>
            <select
              id="branch"
              value={formData.branch}
              onChange={(e) => handleInputChange("branch", e.target.value)}
              className="w-full px-3 py-1 rounded-lg border border-[#d1d1d1] bg-white text-[#323130] focus:border-[#4169E1] focus:outline-none transition-colors appearance-none"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%234169E1' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 12px center",
                backgroundSize: "20px",
              }}
            >
              <option value="">กรุณาเลือกสาขา</option>
              <option value="headquarters">สำนักงานใหญ่</option>
              <option value="branch1">สาขา 1</option>
              <option value="branch2">สาขา 2</option>
              <option value="other">อื่นๆ</option>
            </select>
          </div>

          {/* Address for Tax Section */}
          <div className="space-y-4 pt-4">
            <h2 className="text-xl font-bold text-[#323130]">
              ที่อยู่สำหรับจัดส่งใบกำกับภาษี
            </h2>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  id="sameAddress"
                  name="addressType"
                  checked={useAddressForTax === true}
                  onChange={() => handleAddressTypeChange(true)}
                  disabled={loading}
                  className="w-5 h-5 text-[#4169E1] border-[#d1d1d1] focus:ring-[#4169E1]"
                />
                <Label htmlFor="sameAddress" className="text-[#323130] font-medium cursor-pointer">
                  ใช้ที่อยู่สำหรับออกใบกำกับภาษี
                </Label>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="radio"
                  id="differentAddress"
                  name="addressType"
                  checked={useAddressForTax === false}
                  onChange={() => handleAddressTypeChange(false)}
                  disabled={loading}
                  className="w-5 h-5 text-[#4169E1] border-[#d1d1d1] focus:ring-[#4169E1]"
                />
                <Label htmlFor="differentAddress" className="text-[#323130] font-medium cursor-pointer">
                  ใช้ที่อยู่อื่น
                </Label>
              </div>
            </div>

            {useAddressForTax === false && (
              <div className="space-y-4">
                <Label htmlFor="taxInvoiceDeliveryAddress" className="text-[#323130] font-medium">
                  ที่อยู่สำหรับจัดส่งใบกำกับภาษี <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="taxInvoiceDeliveryAddress"
                  type="text"
                  value={taxInvoiceDeliveryData.address}
                  onChange={(e) => handleTaxDeliveryInputChange("address", e.target.value)}
                  placeholder="กรอกที่อยู่สำหรับจัดส่งใบกำกับภาษี"
                  disabled={loading}
                  className="bg-white border-[#d1d1d1] text-[#323130]"
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <ThaiLocationSelect
                      label="จังหวัด"
                      value={taxLocationData.province}
                      onChange={(value, id) => {
                        handleTaxProvinceChange(value, id)
                        setError("")
                      }}
                      options={taxProvinces}
                      placeholder="เลือกจังหวัด"
                      disabled={loading || taxLocationLoading.provinces}
                      loading={taxLocationLoading.provinces}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="taxInvoiceDeliveryPostalCode" className="text-[#323130] font-medium">
                      รหัสไปรษณีย์ <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="taxInvoiceDeliveryPostalCode"
                      type="text"
                      value={taxLocationData.postalCode}
                      readOnly
                      placeholder="จะกรอกอัตโนมัติเมื่อเลือกตำบล"
                      disabled
                      className="bg-gray-50 border-[#d1d1d1] text-[#323130]"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <ThaiLocationSelect
                      label="เขต / อำเภอ"
                      value={taxLocationData.amphure}
                      onChange={(value, id) => {
                        handleTaxAmphureChange(value, id)
                        setError("")
                      }}
                      options={taxAmphures}
                      placeholder={taxLocationData.provinceId ? "เลือกเขต / อำเภอ" : "กรุณาเลือกจังหวัดก่อน"}
                      disabled={loading || !taxLocationData.provinceId || taxLocationLoading.amphures}
                      loading={taxLocationLoading.amphures}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <ThaiLocationSelect
                      label="แขวง / ตำบล"
                      value={taxLocationData.tambon}
                      onChange={(value, id) => {
                        handleTaxDistrictChange(value, id)
                        setError("")
                      }}
                      options={taxDistricts}
                      placeholder={taxLocationData.amphureId ? "เลือกแขวง / ตำบล" : "กรุณาเลือกเขต / อำเภอก่อน"}
                      disabled={loading || !taxLocationData.amphureId || taxLocationLoading.districts}
                      loading={taxLocationLoading.districts}
                      required
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Document Upload Section */}
          <div className="space-y-4 pt-6">
            <div className="flex items-center gap-2 pb-2">
              <FileText className="w-5 h-5 text-[#4169E1]" />
              <h3 className="text-lg font-semibold text-[#323130]">เอกสารประกอบการยืนยันตัวตน</h3>
            </div>

            <DocumentUploader
              documentType="company_registration"
              label="หนังสือจดทะเบียนบริษัท"
              description="กรุณาอัพโหลดสำเนาหนังสือจดทะเบียนบริษัท (ไฟล์ภาพหรือ PDF, ขนาดไม่เกิน 10MB)"
              existingDocument={uploadedDocuments.companyRegistration}
              onUploadSuccess={(doc) => {
                setUploadedDocuments(prev => ({ ...prev, companyRegistration: doc }));
                setRefreshDocuments(prev => prev + 1);
              }}
              disabled={loading}
            />

            <DocumentUploader
              documentType="director_id_card"
              label="บัตรประชาชนกรรมการผู้มีอำนาจ"
              description="กรุณาอัพโหลดสำเนาบัตรประชาชนของกรรมการผู้มีอำนาจลงนาม (ไฟล์ภาพหรือ PDF, ขนาดไม่เกิน 10MB)"
              existingDocument={uploadedDocuments.directorIdCard}
              onUploadSuccess={(doc) => {
                setUploadedDocuments(prev => ({ ...prev, directorIdCard: doc }));
                setRefreshDocuments(prev => prev + 1);
              }}
              disabled={loading}
            />

            <DocumentUploader
              documentType="additional_document"
              label="เอกสารเพิ่มเติม (ถ้ามี)"
              description="เอกสารอื่นๆ เช่น หนังสือรับรองบริษัท, หนังสือมอบอำนาจ (ถ้ามี)"
              existingDocument={uploadedDocuments.additionalDocument}
              onUploadSuccess={(doc) => {
                setUploadedDocuments(prev => ({ ...prev, additionalDocument: doc }));
                setRefreshDocuments(prev => prev + 1);
              }}
              disabled={loading}
            />

            <DocumentList 
              key={refreshDocuments}
              onRefresh={() => setRefreshDocuments(prev => prev + 1)}
              onDocumentDeleted={(documentType) => {
                // Clear the corresponding uploaded document state
                setUploadedDocuments(prev => {
                  const newState = { ...prev };
                  if (documentType === 'company_registration') newState.companyRegistration = undefined;
                  if (documentType === 'director_id_card') newState.directorIdCard = undefined;
                  if (documentType === 'additional_document') newState.additionalDocument = undefined;
                  return newState;
                });
                setRefreshDocuments(prev => prev + 1);
              }}
              showActions={true}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4 pt-6">
            <Button
              onClick={handleBack}
              variant="outline"
              className="flex-1 border-[#d1d1d1] text-[#323130] hover:bg-[#f5f5f5] py-6 text-lg"
              disabled={loading}
            >
              ยกเลิก
            </Button>
            <Button
              onClick={handleNext}
              className="flex-1 bg-[#4169E1] hover:bg-[#3155c8] text-white py-6 text-lg"
              disabled={loading}
            >
              {loading ? "กำลังบันทึก..." : "บันทึกและไปต่อ"}
            </Button>
          </div>
        </div>
      </div>
    </AuthLayout>
  )
}
